import { Modal, QRCode, Typography, Space, Button, message } from "antd";
import { DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text, Paragraph } = Typography;

type Props = {
  requestId?: string;
  number?: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

/**
 * Shows a QR code that encodes the request's URL. Scanning it opens the normal
 * (authenticated) request page. Generated fully client-side via antd's QRCode —
 * no backend, no extra dependency.
 */
const QrCodeModal = (props: Props) => {
  const { t } = useTranslation();

  // Encode the app-origin URL so the QR points at wherever the app is served
  // (matches the "Open request" links used in notification emails in prod).
  const url = props.requestId
    ? `${window.location.origin}/requests/${props.requestId}`
    : "";

  const canvasId = `qr-canvas-${props.requestId ?? "x"}`;
  const fileLabel = props.number || props.requestId || "request";

  const getCanvas = (): HTMLCanvasElement | null =>
    document
      .getElementById(canvasId)
      ?.querySelector<HTMLCanvasElement>("canvas") ?? null;

  const handleDownload = () => {
    const canvas = getCanvas();
    if (!canvas) {
      message.error(t("Could not generate the QR image."));
      return;
    }
    const link = document.createElement("a");
    link.download = `request-${fileLabel}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const canvas = getCanvas();
    if (!canvas) {
      message.error(t("Could not generate the QR image."));
      return;
    }
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank", "width=420,height=560");
    if (!win) {
      message.warning(t("Please allow pop-ups to print."));
      return;
    }
    win.document.write(
      `<html><head><title>QR ${fileLabel}</title></head>
       <body style="margin:0;padding:40px;text-align:center;font-family:Arial,Helvetica,sans-serif;">
         <h2 style="margin:0 0 8px;font-size:18px;color:#111;">${fileLabel}</h2>
         <img src="${dataUrl}" style="width:280px;height:280px;" alt="QR" />
         <p style="color:#888;font-size:12px;word-break:break-all;margin-top:12px;">${url}</p>
         <script>window.onload=function(){window.focus();window.print();}</script>
       </body></html>`
    );
    win.document.close();
  };

  return (
    <Modal
      title={
        <span>
          {t("Request QR Code")}
          {props.number ? ` · ${props.number}` : ""}
        </span>
      }
      open={props.visible}
      onCancel={() => props.setVisible(false)}
      centered
      footer={[
        <Button key="close" onClick={() => props.setVisible(false)}>
          {t("Close")}
        </Button>,
        <Button
          key="print"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          disabled={!url}
        >
          {t("Print")}
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          disabled={!url}
        >
          {t("Download PNG")}
        </Button>,
      ]}
    >
      <Space
        direction="vertical"
        align="center"
        style={{ width: "100%", padding: "12px 0" }}
      >
        <Text type="secondary">{t("Scan to open this request")}</Text>
        {url ? (
          <div id={canvasId}>
            <QRCode value={url} size={224} errorLevel="M" bordered />
          </div>
        ) : null}
        <Paragraph
          copyable={{ text: url }}
          style={{
            marginBottom: 0,
            maxWidth: 320,
            textAlign: "center",
            wordBreak: "break-all",
            fontSize: 12,
            color: "#8c8c8c",
          }}
        >
          {url}
        </Paragraph>
      </Space>
    </Modal>
  );
};

export default QrCodeModal;
